import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { pathByRouteName } from '../../../util/routes';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldPhoneNumberInput,
  FieldCheckbox,
  ExternalLink,
  ValidationError,
} from '../../../components';

import css from './ContactForm.module.css';

// Mismos límites que server/api/contact.js (MAX_LENGTHS). El servidor recorta
// igualmente: esto es solo para dar feedback antes de enviar.
const MAX_LENGTHS = {
  email: 254,
  phone: 32,
  consulta: 2000,
};

// Móvil español: 9 dígitos, empieza por 6 o 7, SIN prefijo. El usuario no
// escribe el +34; lo antepone el servidor antes de llamar al CRM
// (ver server/api/contact.js, PHONE_COUNTRY_PREFIX).
const SPANISH_MOBILE_RE = /^[67]\d{8}$/;

// Nombre de la ruta en src/routing/routeConfiguration.js (path '/privacy-policy').
// El path se deriva de ahí con pathByRouteName: si la ruta cambia, el enlace
// sigue apuntando bien sin tocar este fichero.
const PRIVACY_POLICY_ROUTE_NAME = 'PrivacyPolicyPage';

const ContactFormComponent = props => {
  const intl = useIntl();
  // Hooks en el cuerpo del componente, no dentro del render prop de FinalForm.
  const routeConfiguration = useRouteConfiguration();
  const privacyPolicyPath = pathByRouteName(PRIVACY_POLICY_ROUTE_NAME, routeConfiguration);

  // El enlace abre en pestaña nueva (ExternalLink pone target="_blank" y
  // rel="noopener noreferrer") para no perder lo ya escrito en el formulario.
  // NamedLink no vale aquí: construye los props del <a> desde una lista cerrada
  // y descarta `target`. Aun así la ruta NO va a mano — sale de
  // routeConfiguration vía pathByRouteName.
  const privacyPolicyLink = (
    <ExternalLink href={privacyPolicyPath} className={css.consentLink}>
      <FormattedMessage id="ContactPage.consentPrivacyLink" />
    </ExternalLink>
  );

  return (
    <FinalForm
      {...props}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          sendError,
          errors,
          submitFailed,
        } = formRenderProps;

        // ---- Validadores (src/util/validators.js) ----
        // El email es OPCIONAL: solo se valida el formato si el usuario
        // escribe algo. validators.emailFormatValid rechaza el valor vacío,
        // así que se envuelve para dejar pasar el campo sin rellenar.
        const emailFormat = validators.emailFormatValid(
          intl.formatMessage({ id: 'ContactPage.emailInvalid' })
        );
        const emailValidIfPresent = value => (value ? emailFormat(value) : undefined);

        // FieldPhoneNumberInput no valida por sí solo (solo formatea), así que
        // el validate va a mano. Es el ÚNICO campo obligatorio de texto.
        const phoneRequired = validators.required(
          intl.formatMessage({ id: 'ContactPage.phoneRequired' })
        );
        const phoneInvalidMessage = intl.formatMessage({ id: 'ContactPage.phoneInvalid' });
        const phoneSpanishMobile = value => {
          const digits = typeof value === 'string' ? value.replace(/\s/g, '') : '';
          return SPANISH_MOBILE_RE.test(digits) ? undefined : phoneInvalidMessage;
        };

        const consultaMaxLength = validators.maxLength(
          intl.formatMessage(
            { id: 'ContactPage.consultaTooLong' },
            { maxLength: MAX_LENGTHS.consulta }
          ),
          MAX_LENGTHS.consulta
        );

        // El checkbox de consentimiento es un campo array en final-form.
        const consentRequired = validators.requiredFieldArrayCheckbox(
          intl.formatMessage({ id: 'ContactPage.consentRequired' })
        );

        const consentErrorId = formId ? `${formId}.consentError` : 'consentError';
        const showConsentError = submitFailed && !!errors?.consent;

        const classes = classNames(rootClassName || css.root, className);
        // Solo se deshabilita mientras se envía. NO se deshabilita por
        // formulario inválido a propósito: un botón muerto no explica qué
        // falta. Si hay errores, final-form bloquea el submit, marca los
        // campos como touched y cada uno pinta su error inline. Mismo criterio
        // que SignupForm (ver su comentario sobre los términos).
        const submitDisabled = inProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FieldPhoneNumberInput
              className={css.field}
              id={formId ? `${formId}.phone` : 'phone'}
              name="phone"
              autoComplete="tel"
              label={intl.formatMessage({ id: 'ContactPage.phoneLabel' })}
              placeholder={intl.formatMessage({ id: 'ContactPage.phonePlaceholder' })}
              maxLength={MAX_LENGTHS.phone}
              validate={validators.composeValidators(phoneRequired, phoneSpanishMobile)}
            />

            <FieldTextInput
              className={css.field}
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={intl.formatMessage({ id: 'ContactPage.emailLabel' })}
              placeholder={intl.formatMessage({ id: 'ContactPage.emailPlaceholder' })}
              maxLength={MAX_LENGTHS.email}
              validate={emailValidIfPresent}
            />

            <FieldTextInput
              className={classNames(css.field, css.consulta)}
              type="textarea"
              id={formId ? `${formId}.consulta` : 'consulta'}
              name="consulta"
              label={intl.formatMessage({ id: 'ContactPage.consultaLabel' })}
              placeholder={intl.formatMessage({ id: 'ContactPage.consultaPlaceholder' })}
              maxLength={MAX_LENGTHS.consulta}
              /* rows={1}: autosize fija `height` inline a partir del número de
                 filas, y con rows={2} esa altura inline (2 líneas) superaba el
                 min-height y dejaba la caja más alta que los inputs. Con 1 fila
                 manda el min-height de .consulta, que iguala la altura del
                 input. Sigue creciendo al escribir. */
              rows={1}
              validate={consultaMaxLength}
            />

            {/* Honeypot: invisible para humanos, tentador para bots. Si llega
                con contenido, server/api/contact.js descarta el envío sin
                llamar al CRM. Fuera del flujo de tabulación y oculto a los
                lectores de pantalla, no con display:none (algunos bots lo
                detectan y lo saltan). */}
            <div className={css.honeypot} aria-hidden="true">
              <FieldTextInput
                type="text"
                id={formId ? `${formId}.website` : 'website'}
                name="website"
                label="Website"
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            {/* FieldCheckbox NO pinta mensajes de validación: a diferencia de
                FieldTextInput, no monta ValidationError (ver su render: solo
                input + label). Sin esto, el submit se bloqueaba en silencio.
                Lo añadimos aquí, con el MISMO componente que usan los demás
                campos, sin tocar FieldCheckbox (lo comparten el alta y los
                filtros del buscador).

                Accesibilidad: lo ideal sería aria-describedby en el <input>,
                pero FieldCheckbox no reenvía props al input (pasa {...rest} a
                <Field> y su render solo lee input y disabled). Se asocia por
                tanto con un grupo que describe el mensaje, y el contenedor del
                error es role="alert" para que se anuncie al aparecer. */}
            <div
              className={css.consentGroup}
              role="group"
              aria-describedby={showConsentError ? consentErrorId : undefined}
            >
              <FieldCheckbox
                className={css.consent}
                /* Texto legal: escala de texto secundario, no de copy. */
                textClassName={css.consentText}
                /* Casilla hueca sin marcar. Ver .consentBox en el CSS: es el
                   único gancho que FieldCheckbox expone hacia el SVG. */
                svgClassName={css.consentBox}
                id={formId ? `${formId}.consent` : 'consent'}
                name="consent"
                value="yes"
                label={
                  <FormattedMessage id="ContactPage.consentLabel" values={{ privacyPolicyLink }} />
                }
                validate={consentRequired}
              />
              <div id={consentErrorId} role="alert">
                {/* touched se ata a submitFailed, no al blur: el error aparece
                    al intentar enviar, nunca al cargar ni al pasar por encima. */}
                <ValidationError fieldMeta={{ touched: submitFailed, error: errors?.consent }} />
              </div>
            </div>

            {sendError ? (
              <p className={css.error} role="alert">
                {sendError}
              </p>
            ) : null}

            <PrimaryButton
              className={css.submitButton}
              type="submit"
              inProgress={inProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="ContactPage.submitButton" />
            </PrimaryButton>
          </Form>
        );
      }}
    />
  );
};

/**
 * Contact form for ContactPage.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - Overrides css.root
 * @param {string} props.className - Extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the submit is in progress
 * @param {string} props.sendError - Localized error message to show above the button.
 *   NOTE: this prop must NOT be called `submitError`. react-final-form owns that
 *   name in its form state as a getter-only property, and passing a prop with
 *   the same name makes it throw on mount ("Cannot set property submitError of
 *   #<Object> which has only a getter"). Same reason PasswordRecoveryForm calls
 *   its own prop `recoveryError`.
 * @param {Function} props.onSubmit - Submit handler
 * @returns {JSX.Element}
 */
const ContactForm = props => <ContactFormComponent {...props} />;

export default ContactForm;
